import { memo, useCallback, useMemo, useState } from "react";
import { formatISO } from "date-fns";
import { InvoiceStatus as InvoiceStatusEnum } from "@common/types/invoice";
import { currencyFormatter } from "@common/helpers/currency";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { VStack } from "@design-system/VStack/VStack";
import { useLockDateState } from "@api/lock.api";
import { getInvoiceStatusLabel } from "./getInvoiceStatusLabel";
import { InvoiceStatus } from "./InvoiceStatus";
import { invoiceBooksToBadDebt } from "./invoiceBooksToBadDebt";
import { ConfirmInvoiceStatusDateModal } from "./ConfirmInvoiceStatusDateModal";
import {
    getInvoiceStatusTransitionDisabledReason,
    QuickBooksSettlementExtent,
} from "./getInvoiceStatusTransitionDisabledReason";
import { InvoicePaymentLine } from "./buildInvoicePaymentAllocations";
import { RecordInvoicePaymentModal } from "./RecordInvoicePaymentModal";
import { InvoiceScheduleTeardown } from "./invoiceScheduleTeardown";

const INVOICE_STATUSES = [
    InvoiceStatusEnum.DRAFT,
    InvoiceStatusEnum.OPEN,
    InvoiceStatusEnum.PAID,
    InvoiceStatusEnum.VOID,
    InvoiceStatusEnum.UNCOLLECTABLE,
];

interface StatusChange {
    status: InvoiceStatusEnum;
    uncollectableAt?: string | null;
    voidedAt?: string | null;
    paidAt?: string | null;
}

// Builds the onValueChange payload for a status transition: the date field
// belonging to the new status is set to `date`, while the sibling date fields
// are cleared only when they were populated by the previous status.
const buildStatusChange = ({
    status,
    currentStatus,
    date,
}: {
    status: InvoiceStatusEnum;
    currentStatus: InvoiceStatusEnum;
    date?: string;
}): StatusChange => {
    const clearSiblingDate = (statusWithDate: InvoiceStatusEnum) =>
        currentStatus === statusWithDate ? null : undefined;

    return {
        status,
        paidAt:
            status === InvoiceStatusEnum.PAID
                ? date
                : clearSiblingDate(InvoiceStatusEnum.PAID),
        uncollectableAt:
            status === InvoiceStatusEnum.UNCOLLECTABLE
                ? date
                : clearSiblingDate(InvoiceStatusEnum.UNCOLLECTABLE),
        voidedAt:
            status === InvoiceStatusEnum.VOID
                ? date
                : clearSiblingDate(InvoiceStatusEnum.VOID),
    };
};

const SCHEDULED_BAD_DEBT_COPY =
    "Revenue recognized before the uncollectible date will be booked to bad debt expense, and any active recognition schedules will stop on that date.";

const getOpenBalanceBadDebtCopy = (openAmount?: number): string => {
    if (openAmount != null) {
        return `The open balance of ${currencyFormatter.format(openAmount)} will be booked to bad debt expense on the uncollectible date.`;
    }
    return "The open balance will be booked to bad debt expense on the uncollectible date.";
};

const getScheduledBadDebtCopy = ({
    unscheduledOpenAmount,
}: InvoiceScheduleTeardown): string => {
    if (unscheduledOpenAmount > 0) {
        return `The open balance of ${currencyFormatter.format(unscheduledOpenAmount)} not covered by a recognition schedule will be booked to bad debt expense on the uncollectible date. ${SCHEDULED_BAD_DEBT_COPY}`;
    }
    return SCHEDULED_BAD_DEBT_COPY;
};

interface InvoiceStatusSelectProps {
    value: InvoiceStatusEnum;
    invoiceNumber?: string | null;
    uncollectableAt?: string | null;
    voidedAt?: string | null;
    /**
     * Current open balance. Shown in the uncollectible confirmation so the
     * user sees exactly what will be booked to Bad Debt Expense. Only rendered
     * for invoices that actually post a bad-debt journal entry (see
     * `currency`).
     */
    openAmount?: number;
    /**
     * Invoice currency (e.g. "usd"). Only USD invoices post a bad-debt journal
     * entry when marked uncollectible; for any other currency the confirmation
     * copy drops the bad-debt claim entirely (mirrors the backend JE builder,
     * which skips non-USD invoices). See `invoiceBooksToBadDebt`.
     */
    currency?: string;
    /** Set when a recognition schedule has posted revenue for this invoice. */
    scheduleTeardown?: InvoiceScheduleTeardown | null;
    /**
     * Native (Kick-created) invoices only: the user-facing business date
     * (YYYY-MM-DD). Used to enforce that the void/uncollectible date can
     * never be set before the invoice date (same day is allowed).
     * Undefined/null for every other invoice source, where no such
     * constraint applies.
     */
    invoiceDate?: string | null;
    /**
     * The invoice's entity, used to look up its period lock date so the
     * void/uncollectible date picker also disables dates in a locked period,
     * not only dates before the invoice date.
     */
    entityId: number;
    onValueChange: (params: StatusChange) => void;
    /**
     * True when any invoice line has matchedAmount > 0. Used to disable the
     * Paid → Open option when reopening must happen via unmatching instead
     * (mirrors backend `canTransitionInvoiceStatus` in
     * common/helpers/invoice-status-machine.ts).
     */
    hasMatches?: boolean;
    /**
     * False when the invoice has no counterparty assigned. Only Draft
     * invoices may lack one, so this disables every transition off Draft
     * (mirrors backend `canTransitionInvoiceStatus`).
     */
    hasCounterparty?: boolean;
    quickBooksSettlement?: QuickBooksSettlementExtent;
    /**
     * When marking an Open invoice paid, the user must create a cash
     * transaction and match it instead of flipping status alone.
     */
    recordPayment?: {
        invoiceId: string;
        entityId: number;
        openAmount: number;
        lines?: InvoicePaymentLine[];
        counterpartyId?: string | null;
        onRecorded: () => void;
    };
}

type PendingConfirmation =
    | { kind: "uncollectableAt" }
    | { kind: "voidedAt" }
    | { kind: "recordPayment" }
    | null;

export const InvoiceStatusSelect = memo(
    ({
        value,
        invoiceNumber,
        uncollectableAt,
        voidedAt,
        openAmount,
        currency,
        scheduleTeardown,
        invoiceDate,
        entityId,
        onValueChange,
        hasMatches = false,
        hasCounterparty = true,
        quickBooksSettlement,
        recordPayment,
    }: InvoiceStatusSelectProps) => {
        const [pendingConfirmation, setPendingConfirmation] =
            useState<PendingConfirmation>(null);
        const { lockDate, isResolved: isLockResolved } =
            useLockDateState(entityId);

        const triggerContent = useMemo(
            () => <InvoiceStatus status={value} showText />,
            [value],
        );

        const statusItems = useMemo<Array<PickerItemData<InvoiceStatusEnum>>>(
            () =>
                INVOICE_STATUSES.map((status) => {
                    const disabledReason =
                        getInvoiceStatusTransitionDisabledReason({
                            from: value,
                            to: status,
                            hasMatches,
                            hasCounterparty,
                            quickBooksSettlement,
                        });
                    return {
                        value: status,
                        label: getInvoiceStatusLabel(status),
                        leading: <InvoiceStatus status={status} />,
                        disabled: disabledReason !== null,
                        disabledReason,
                    };
                }),
            [value, hasMatches, hasCounterparty, quickBooksSettlement],
        );

        const handleStatusChange = useCallback(
            (newStatus: InvoiceStatusEnum | null) => {
                if (!newStatus) {
                    return;
                }

                if (newStatus === InvoiceStatusEnum.UNCOLLECTABLE) {
                    // uncollectableAt must never be changed once set — once
                    // present, re-use it instead of prompting again.
                    if (uncollectableAt) {
                        onValueChange(
                            buildStatusChange({
                                status: InvoiceStatusEnum.UNCOLLECTABLE,
                                currentStatus: value,
                            }),
                        );
                        return;
                    }
                    setPendingConfirmation({ kind: "uncollectableAt" });
                    return;
                }

                if (newStatus === InvoiceStatusEnum.VOID) {
                    // voidedAt must never be changed once set — once present,
                    // re-use it instead of prompting again.
                    if (voidedAt) {
                        onValueChange(
                            buildStatusChange({
                                status: InvoiceStatusEnum.VOID,
                                currentStatus: value,
                            }),
                        );
                        return;
                    }
                    setPendingConfirmation({ kind: "voidedAt" });
                    return;
                }

                if (newStatus === InvoiceStatusEnum.PAID) {
                    if (
                        value === InvoiceStatusEnum.OPEN &&
                        recordPayment != null &&
                        recordPayment.openAmount > 0
                    ) {
                        setPendingConfirmation({ kind: "recordPayment" });
                        return;
                    }
                    onValueChange(
                        buildStatusChange({
                            status: InvoiceStatusEnum.PAID,
                            currentStatus: value,
                            date: formatISO(new Date(), {
                                representation: "date",
                            }),
                        }),
                    );
                    return;
                }

                onValueChange(
                    buildStatusChange({
                        status: newStatus,
                        currentStatus: value,
                    }),
                );
            },
            [onValueChange, value, uncollectableAt, voidedAt, recordPayment],
        );

        const handleCancelConfirmation = useCallback(() => {
            setPendingConfirmation(null);
        }, []);

        const handleConfirmDate = useCallback(
            (date: string) => {
                if (pendingConfirmation?.kind === "uncollectableAt") {
                    onValueChange(
                        buildStatusChange({
                            status: InvoiceStatusEnum.UNCOLLECTABLE,
                            currentStatus: value,
                            date,
                        }),
                    );
                } else if (pendingConfirmation?.kind === "voidedAt") {
                    onValueChange(
                        buildStatusChange({
                            status: InvoiceStatusEnum.VOID,
                            currentStatus: value,
                            date,
                        }),
                    );
                }
                setPendingConfirmation(null);
            },
            [pendingConfirmation, onValueChange, value],
        );

        return (
            <>
                <Picker.Select
                    items={statusItems}
                    value={value}
                    onValueChange={handleStatusChange}
                    renderValue={() => triggerContent}
                    chevron="none"
                    trigger={<Picker.Button variant="secondary" size="sm" />}
                />
                <ConfirmInvoiceStatusDateModal
                    show={pendingConfirmation?.kind === "uncollectableAt"}
                    title={
                        invoiceNumber
                            ? `Mark invoice ${invoiceNumber} uncollectible?`
                            : "Mark invoice uncollectible?"
                    }
                    question={
                        <VStack gap={16}>
                            {currency != null &&
                                invoiceBooksToBadDebt(currency) && (
                                    <p>
                                        {scheduleTeardown
                                            ? getScheduledBadDebtCopy(
                                                  scheduleTeardown,
                                              )
                                            : getOpenBalanceBadDebtCopy(
                                                  openAmount,
                                              )}
                                    </p>
                                )}
                            <p>
                                Uncollectible invoices can be matched or marked
                                paid later if recovered.
                            </p>
                        </VStack>
                    }
                    dateLabel="Uncollectible date"
                    minDate={invoiceDate ?? undefined}
                    invoiceDate={invoiceDate}
                    lockDate={lockDate}
                    isLockResolved={isLockResolved}
                    onCancel={handleCancelConfirmation}
                    onConfirm={handleConfirmDate}
                />
                <ConfirmInvoiceStatusDateModal
                    show={pendingConfirmation?.kind === "voidedAt"}
                    title={
                        invoiceNumber
                            ? `Mark invoice ${invoiceNumber} void?`
                            : "Mark invoice void?"
                    }
                    description={
                        scheduleTeardown
                            ? "Recognized revenue will be reversed and any active recognition schedules stopped. This action can't be undone."
                            : "This action can't be undone."
                    }
                    dateFieldLabel="Void date"
                    minDate={invoiceDate ?? undefined}
                    invoiceDate={invoiceDate}
                    lockDate={lockDate}
                    isLockResolved={isLockResolved}
                    onCancel={handleCancelConfirmation}
                    onConfirm={handleConfirmDate}
                />
                {recordPayment != null && (
                    <RecordInvoicePaymentModal
                        show={pendingConfirmation?.kind === "recordPayment"}
                        invoiceId={recordPayment.invoiceId}
                        invoiceNumber={invoiceNumber}
                        invoiceEntityId={recordPayment.entityId}
                        openAmount={recordPayment.openAmount}
                        lines={recordPayment.lines}
                        counterpartyId={recordPayment.counterpartyId}
                        onCancel={handleCancelConfirmation}
                        onRecorded={recordPayment.onRecorded}
                    />
                )}
            </>
        );
    },
);

InvoiceStatusSelect.displayName = "InvoiceStatusSelect";
